import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getPaginatedPartManufacturers,
  getPartManufacturer,
  getPartManufacturersByIds,
  getPartManufacturersBySearch,
} from "@/api/resources/partManufacturers/api";
import i18n from "@/providers/i18n";
import { type QueryPaginationFilters } from "@/utils/pagination";

import type { SearchOptionFilters } from "@/utils/api";

const entity = i18n.t("part-manufacturer");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("part-manufacturers");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const partManufacturersQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "partManufacturers"],
  lists: () => [...partManufacturersQueryKeys.all(), "list"],
  listByIds: (ids?: string[]) => [...partManufacturersQueryKeys.lists(), "byIds", ids],
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...partManufacturersQueryKeys.lists(),
    "paginated",
    queryKeyPaginationFilters,
  ],
  listBySearch: (searchOptionFilters: SearchOptionFilters) => [
    ...partManufacturersQueryKeys.lists(),
    "bySearch",
    searchOptionFilters,
  ],
  byId: (id?: string) => [...partManufacturersQueryKeys.all(), "byId", id],
};
export const partManufacturersQueries = {
  listByIds: (ids?: string[]) =>
    queryOptions({
      queryKey: [...partManufacturersQueryKeys.listByIds(ids)],
      queryFn: () =>
        getPartManufacturersByIds({
          keyIn: "id",
          idsIn: ids,
        }),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...partManufacturersQueryKeys.listPaginated(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedPartManufacturers(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listBySearch: (searchOptionFilters: SearchOptionFilters) =>
    queryOptions({
      queryKey: [...partManufacturersQueryKeys.listBySearch(searchOptionFilters)],
      queryFn: () => getPartManufacturersBySearch(searchOptionFilters),
      placeholderData: (prev) => prev, // ! this prevents flashing of results when using search in selectors
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...partManufacturersQueryKeys.byId(id)],
      queryFn: id ? () => getPartManufacturer({ match: { id } }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
